
import { Component, Emit, Mixins, Prop } from 'vue-property-decorator'
import {
  DesktopIconsItem as HeaderDesktopIconsItem,
  Icon,
  MALL_ICONS,
} from '@mg/micro/vue2'
import { CartDropdown } from '@mg/micro/vue2/CartDropdown'
import type {
  CartDropdownArticle,
  CartDropdownData,
  CartDropdownPartnerGroup,
} from '@mg/micro/vue2/CartDropdown'
import cloneDeep from 'lodash/cloneDeep'
import reverse from 'lodash/reverse'
import { getImageUrl, ImageSize } from '@mg/web-components/utils/image'
import Price from '@mg/web-components/Atoms/Price'
import { GtmEventTypes } from '~/utils/tracking/gtm'
import {
  GQL_LOCAL_GET_CART,
  GQL_LOCAL_MUTATION_AMOUNT_CART,
  GQL_LOCAL_MUTATION_DELETE_CART,
} from '~/graphql/gqlLocal/localCart'
import { MutationOptions } from '~/node_modules/apollo-client'
import { ApolloHelpers } from '~/node_modules/@nuxtjs/apollo'
import {
  GQLLocalCart,
  GQLLocalCartArticle,
  GQLLocalCartMarketplacePartner,
  GQLLocalMutationChangeAmountArgs,
  GQLLocalMutationDeleteCartArgs,
  GQLLocalOpenHeaderEnum,
} from '~/graphql/__generated__/interfacesLocal'
import ROUTES from '~/config/routes'
import AppLink from '~/components/AppLink.vue'
import MarxyTrackingMixin from '~/utils/marxy/MarxyTrackingMixin'
import { YottlyEventTypes } from '~/utils/tracking/yottly'
import { createUrl } from '~/utils/routes'
import HeaderCartBundlesWrap from '~/components/header/HeaderCartBundlesWrap.vue'
import { RemoveFromCartData } from '~/utils/tracking/gtm/cart'

interface CartDropdownArticleWithRaw extends CartDropdownArticle {
  /** original GraphQL article, forwarded to HeaderCartBundlesWrap via the bundles-slot scope */
  raw: GQLLocalCartArticle
}

@Component({
  apollo: {
    cart: {
      fetchPolicy: 'cache-only',
      prefetch: false,
      query: GQL_LOCAL_GET_CART,
    },
  },
  components: {
    HeaderCartBundlesWrap,
    AppLink,
    CartDropdown,
    HeaderDesktopIconsItem,
    Icon,
    Price,
  },
  mixins: [MarxyTrackingMixin],
})
export default class HeaderCart extends Mixins(MarxyTrackingMixin) {
  @Prop(Boolean) isOpenState!: boolean

  MALL_ICONS = MALL_ICONS
  cart: GQLLocalCart
  error: ApolloHelpers | null = null
  isLoadingItems: Record<string, boolean> = {}
  routes = ROUTES

  @Emit('changeTab')
  changeTab(tabName: null | GQLLocalOpenHeaderEnum) {
    return tabName
  }

  onGoToCartClick() {
    this.$router.push({ path: createUrl(ROUTES.cart, this.$shopConfig.shopId) })
  }

  get cartArticles() {
    const cartArticles = reverse(
      this.cart.articles.filter((article) => !article?.bundleId)
    )
    return cartArticles.reduce((acc, article) => {
      const key = article.marketplacePartner?.title || 'mall'
      acc[key] = acc[key]
        ? { ...acc[key], articles: [...acc[key].articles, article] }
        : {
            marketplacePartner: article.marketplacePartner,
            articles: [article],
          }
      return acc
    }, {} as { [key: string]: { articles: GQLLocalCartArticle[]; marketplacePartner: GQLLocalCartMarketplacePartner } })
  }

  get cartDropdownData(): CartDropdownData | null {
    if (!this.cart) {
      return null
    }

    const articleGroups: CartDropdownPartnerGroup[] = Object.entries(
      this.cartArticles
    ).map(([key, group]) => ({
      key,
      partnerTitle: group.marketplacePartner?.title,
      partnerUrl: group.marketplacePartner?.url ?? undefined,
      articles: group.articles.map((article) => this.mapArticle(article)),
    }))

    return {
      id: String(this.cart.id ?? ''),
      amount: { pieces: this.cart.amount?.pieces ?? 0 },
      articleGroups,
    }
  }

  private mapArticle(
    article: GQLLocalCartArticle
  ): CartDropdownArticleWithRaw {
    return {
      articleId: article.articleId,
      title: article.simpleTitle ? article.simpleTitle : article.title,
      image: this.getImage(article),
      href: this.url(article),
      availabilityText: article.availability.stock.translationKey,
      price: article.price.actual,
      hasGift: article.hasGift,
      hasVipPrice: article.price.type === 'vip',
      counterValue: Number(article.count),
      counterMin: 0,
      counterMax: this.getCounterMax(article.availability.inStock),
      variants: article.variants ?? undefined,
      isLoading: !!this.isLoadingItems[String(article.articleId)],
      createTime: article.createTime
        ? new Date(article.createTime).getTime()
        : undefined,
      raw: article,
    }
  }

  async apolloMutationCart(data: {
    apolloMutation: MutationOptions
    articleId: GQLLocalCartArticle['articleId']
  }) {
    const { apolloMutation, articleId } = data

    await this.apolloCart(apolloMutation, articleId)
  }

  async apolloCart(
    mutation: MutationOptions,
    articleId: GQLLocalCartArticle['articleId']
  ) {
    try {
      this.error = null
      this.setLoading(articleId, true)

      await this.$apollo.mutate(mutation)
      this.setLoading(articleId, false)
    } catch (error) {
      this.error = error
      this.setLoading(articleId, false)
    }
  }

  private setLoading(
    articleId: GQLLocalCartArticle['articleId'],
    isLoading: boolean
  ) {
    this.isLoadingItems = {
      ...this.isLoadingItems,
      ...{ [articleId]: isLoading },
    }

    this.isLoadingItems = cloneDeep(this.isLoadingItems)
  }

  gaArticle(article: GQLLocalCartArticle) {
    const { articleId, variants } = article

    if (!variants?.[0].variantId) {
      return [
        {
          id: articleId.toString(),
          quantity: 1,
        },
      ]
    }

    return variants.map((variant) => ({
      id: variant.variantId.toString(),
      bonusSetId: articleId,
      quantity: 1,
    }))
  }

  async deleteCart(article: CartDropdownArticleWithRaw) {
    const rawArticle = article.raw
    const { articleId } = rawArticle

    const articles = this.gaArticle(rawArticle)

    this.$pushAnalyticsEvent(GtmEventTypes.RemoveFromCart, {
      articles,
    } as RemoveFromCartData)

    await this.apolloCart(
      {
        mutation: GQL_LOCAL_MUTATION_DELETE_CART,
        variables: {
          articleId,
          cartId: this.cart.id,
        } as GQLLocalMutationDeleteCartArgs,
      },
      articleId
    )

    this.$pushAnalyticsEvent(YottlyEventTypes.CartInteraction, {
      articles: this.cart.articles,
      id: articleId,
    })

    this.marxyTrackCartUpdate(articleId)
  }

  async onCountChange({
    article,
    count,
  }: {
    article: CartDropdownArticleWithRaw
    count: number
  }) {
    const rawArticle = article.raw
    const { articleId } = rawArticle

    if (count === 0) {
      await this.deleteCart(article)
      return
    }

    await this.apolloCart(
      {
        mutation: GQL_LOCAL_MUTATION_AMOUNT_CART,
        variables: {
          amount: count,
          articleId,
          cartId: this.cart.id,
        } as GQLLocalMutationChangeAmountArgs,
      },
      articleId
    )

    this.$pushAnalyticsEvent(YottlyEventTypes.CartInteraction, {
      articles: this.cart.articles,
      id: articleId,
    })

    this.marxyTrackCartUpdate(articleId)
  }

  getImage(article: GQLLocalCartArticle) {
    return getImageUrl(article.mainMediaId!, ImageSize.ICON_LARGE)
  }

  url(article: GQLLocalCartArticle) {
    if (article.articleType === 'bonus_set') {
      return createUrl(ROUTES.bonusSet, this.$shopConfig.shopId, {
        params: {
          productUrl: article.url,
        },
      })
    }

    return `/${article.url}`
  }

  openCart() {
    this.changeTab(GQLLocalOpenHeaderEnum.Cart)
  }

  closedCart() {
    this.changeTab(null)
  }

  get translations() {
    return {
      emptyCart: this.$t('common.txt_cart_navigation_basket') as string,
      deliveryPartner: this.$t(
        'common.cart_preview_delivery_partner'
      ) as string,
      deliveryMall: this.$t(
        'common.cart_preview_delivery_mall_title'
      ) as string,
      goToCart: this.$t('common.cta_cart_go-to') as string,
      informationList: this.$t('common.information_list') as string,
      reservationExpired: this.$t('common.reservation_expired') as string,
      reservationPending: this.$t('common.reservation_pending') as string,
      reservationUrge: this.$t('common.reservation_urge') as string,
    }
  }

  getCounterMax(itemMaxCount: number) {
    const configMax = Number(
      this.$shopConfig.webConfig.cartMaxArticleItems ?? '30'
    )
    if (itemMaxCount <= 0) {
      return 1
    } else if (itemMaxCount > configMax) {
      return configMax
    }
    return itemMaxCount
  }
}
